import OCGatewayClient from "../client/OCGatewayClient";
import { useMutation, useQueryClient } from "@tanstack/react-query";

export interface StarEventParams {
    deviceId: string;
    eventId: number;
}

// star and unstar differ only in the endpoint and the value the optimistic
// update flips to
function useSetEventStarredMutation(isStarred: boolean) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ deviceId, eventId }: StarEventParams) => {
            await OCGatewayClient.request(isStarred ? 'starEvent' : 'unstarEvent', {
                deviceId,
                eventId
            });
            return { deviceId, eventId };
        },
        onMutate: async ({ deviceId, eventId }) => {
            // flip optimistically; cancel refetches that could overwrite the flip
            await queryClient.cancelQueries({
                queryKey: ['getUserStarredEvent', deviceId, eventId]
            });

            const previousStarred = queryClient.getQueryData(['getUserStarredEvent', deviceId, eventId]);
            queryClient.setQueryData(['getUserStarredEvent', deviceId, eventId], { isStarred });

            return { previousStarred };
        },
        onError: (err, { deviceId, eventId }, context) => {
            queryClient.setQueryData(['getUserStarredEvent', deviceId, eventId], context?.previousStarred);
        },
        onSettled: (data, error, { deviceId, eventId }) => {
            queryClient.invalidateQueries({
                queryKey: ['getUserStarredEvent', deviceId, eventId]
            });

            // starred-filtered feeds change membership when a star flips
            queryClient.invalidateQueries({
                queryKey: ['getEvents'],
                predicate: (query) => {
                    const criteria = query.queryKey[1] as any;
                    return criteria?.isStarred !== undefined;
                }
            });
        }
    });
}

export const useStarEventMutation = () => useSetEventStarredMutation(true);
export const useUnstarEventMutation = () => useSetEventStarredMutation(false);
